import { useCallback, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import type { EditableBlock } from '../../lib/types'
import { headingClass, headingLevel, headingTag, pullquoteClass } from '../../lib/blocks'

// The shared type scale (index.css .wr-t-*): body Newsreader with 22px
// between paragraphs, headings in Fraunces at three levels (16px above, 14px
// below), pull quotes centred. The reader uses the same classes. None of
// them clears a wrapped image (Composer lab's wrap): a heading's words flow
// round it like a paragraph's, and a pull quote is its own box (flow-root),
// narrowed beside it.
function styles(block: EditableBlock): string {
  if (block.type === 'heading') return `${headingClass(headingLevel(block))} mb-[14px] mt-4 min-h-[1.2em]`
  if (block.type === 'pullquote') return `${pullquoteClass(headingLevel(block))} flow-root my-10 min-h-[40px] px-2 text-center [text-wrap:pretty]`
  return 'wr-t-body mb-[22px] min-h-[30px] text-foreground'
}

/** The block's accessible name: "Paragraph", "Heading 2", "Pull quote". */
export function editableLabel(block: EditableBlock): string {
  if (block.type === 'heading') return `Heading ${headingLevel(block)}`
  return block.type === 'pullquote' ? 'Pull quote' : 'Paragraph'
}

interface Props {
  block: EditableBlock
  placeholder: string
  /** Extra classes (a paragraph beside a pin gets its rule). */
  className?: string
  /** Narration sync mode: the text is a tap target, not editable. */
  readOnly?: boolean
  onInput: (block: EditableBlock, el: HTMLElement) => void
  onKeyDown: (e: KeyboardEvent<HTMLElement>, block: EditableBlock) => void
  onPaste: (e: ClipboardEvent<HTMLElement>, block: EditableBlock) => void
  onFocus: (block: EditableBlock) => void
  onBlur: (block: EditableBlock) => void
}

/**
 * An uncontrolled contentEditable block. Its markup is written once, when it
 * mounts; typing never round-trips through React. When code (not typing)
 * changes a block's content, the composer remounts it with a new key.
 */
export function EditableBlockView({ block, placeholder, className = '', readOnly = false, onInput, onKeyDown, onPaste, onFocus, onBlur }: Props) {
  const mountedHtml = useRef(block.html)
  const attach = useCallback((el: HTMLElement | null) => {
    if (el && !el.dataset.ready) {
      el.innerHTML = mountedHtml.current
      el.dataset.ready = '1'
    }
  }, [])

  const common = {
    ref: attach,
    contentEditable: !readOnly,
    suppressContentEditableWarning: true,
    role: 'textbox',
    'aria-multiline': block.type === 'text',
    'aria-label': editableLabel(block),
    'aria-readonly': readOnly || undefined,
    'data-block': block.id,
    'data-ph': placeholder,
    spellCheck: true,
    className: `wr-prose outline-none ${styles(block)} ${className}`,
    onInput: (e: React.FormEvent<HTMLElement>) => onInput(block, e.currentTarget),
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => onKeyDown(e, block),
    onPaste: (e: ClipboardEvent<HTMLElement>) => onPaste(e, block),
    onFocus: () => onFocus(block),
    onBlur: () => onBlur(block),
  }
  if (block.type === 'heading') {
    const Tag = headingTag(headingLevel(block))
    return <Tag {...common} />
  }
  return <p {...common} />
}
